import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Rocket, ArrowRight, Hash, Clock, Flame, PackageCheck, Hourglass, ShieldCheck, ShieldAlert, PackageOpen } from 'lucide-react';

const STATUS_META = {
  Draft:      { bg: 'bg-slate-100 text-slate-700',       dot: 'bg-slate-400' },
  Released:   { bg: 'bg-blue-100 text-blue-800',         dot: 'bg-blue-500' },
  InProgress: { bg: 'bg-emerald-100 text-emerald-800',   dot: 'bg-emerald-500' },
};

const priTone = (p) => p <= 2 ? 'bg-red-100 text-red-700 border-red-200'
                      : p <= 4 ? 'bg-amber-100 text-amber-800 border-amber-200'
                      : p <= 7 ? 'bg-slate-100 text-slate-700 border-slate-200'
                      : 'bg-slate-50 text-slate-500 border-slate-200';

const dueTone = (d) => {
  if (!d) return 'text-slate-400';
  const diff = Math.floor((new Date(d) - new Date()) / 86400000);
  if (diff < 0) return 'text-red-600 font-semibold';
  if (diff <= 2) return 'text-amber-600 font-semibold';
  return 'text-slate-500';
};

const WhatsNextHero = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api().get('/dashboard/whats-next?limit=4');
      setData(r.data);
    } catch { /* fail silently — not critical */ }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); const t = setInterval(load, 60_000); return () => clearInterval(t); }, [load]);

  const openWo = (id) => id && navigate(`/work-orders/${id}`);

  if (loading) {
    return (
      <Card className="industrial-card" data-testid="whats-next-hero-loading">
        <CardContent className="p-5 text-sm text-slate-500">Loading next build target…</CardContent>
      </Card>
    );
  }

  if (!data || !data.top) {
    return (
      <Card className="industrial-card border-dashed" data-testid="whats-next-hero-empty">
        <CardContent className="p-5 flex items-center gap-4">
          <PackageOpen className="w-10 h-10 text-slate-300" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-700">Shop floor is clear</p>
            <p className="text-xs text-slate-500">No open Work Orders. Create one from a BOM or spawn one from a Production Order.</p>
          </div>
          <Button variant="outline" size="sm" className="rounded-sm" onClick={() => navigate('/work-orders')} data-testid="whats-next-queue-btn">
            Open Production Queue <ArrowRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  const top = data.top;
  const nextUp = data.next_up || [];
  const totalOpen = data.total_open || 0;
  const topStatus = STATUS_META[top.status] || STATUS_META.Draft;

  return (
    <Card className="industrial-card border-slate-900 border-2 shadow-sm" data-testid="whats-next-hero">
      <CardContent className="p-0">
        <div className="bg-slate-900 text-white px-5 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em]">
            <Rocket className="w-4 h-4 text-amber-300" />
            <span>Build This Next</span>
            <span className="opacity-50">·</span>
            <span className="opacity-70">{totalOpen} open on the floor</span>
          </div>
          <Button size="sm" variant="ghost" onClick={() => navigate('/work-orders')} className="text-white hover:text-amber-300 hover:bg-slate-800 h-7 rounded-sm text-xs" data-testid="whats-next-queue-link">
            Full Queue <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>

        <div className="p-5 flex items-center gap-5 flex-wrap md:flex-nowrap">
          {top.bom_image ? (
            <img src={top.bom_image} alt="" className="w-20 h-20 object-cover rounded-sm border border-slate-200 shrink-0" />
          ) : (
            <div className="w-20 h-20 rounded-sm bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
              <PackageCheck className="w-10 h-10 text-slate-400" />
            </div>
          )}

          <div className="flex-1 min-w-0" data-testid="whats-next-top-info">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <Badge className={`${topStatus.bg} rounded-sm text-xs border-0`}>
                <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${topStatus.dot}`}></span>{top.status}
              </Badge>
              <Badge variant="outline" className={`${priTone(top.priority)} rounded-sm text-xs border`}>
                <Flame className="w-3 h-3 mr-1" /> Priority {top.priority}
              </Badge>
              {top.approval_status === 'approved'
                ? <Badge className="bg-emerald-100 text-emerald-800 rounded-sm text-xs border-0"><ShieldCheck className="w-3 h-3 mr-1" />Approved</Badge>
                : <Badge className="bg-amber-100 text-amber-800 rounded-sm text-xs border-0"><ShieldAlert className="w-3 h-3 mr-1" />Awaiting approval</Badge>
              }
              {top.is_final_product && <Badge variant="outline" className="rounded-sm text-xs">Final Product</Badge>}
            </div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight truncate" data-testid="whats-next-bom-name">{top.bom_name}</h2>
            <div className="flex items-center gap-4 mt-1.5 text-xs text-slate-500 flex-wrap">
              <span className="flex items-center gap-1 mono"><Hash className="w-3 h-3" />{top.wo_number || '—'}</span>
              {top.bom_revision && <span className="mono">rev {top.bom_revision}</span>}
              {top.due_date && (
                <span className={`flex items-center gap-1 ${dueTone(top.due_date)}`}>
                  <Clock className="w-3 h-3" /> Due {top.due_date}
                </span>
              )}
              {top.remaining_quantity !== top.target_quantity && (
                <span className="flex items-center gap-1"><Hourglass className="w-3 h-3" />{top.completed_quantity} of {top.target_quantity} done</span>
              )}
            </div>
          </div>

          <div className="flex flex-col items-end gap-2 shrink-0">
            <div className="text-right">
              <div className="text-xs text-slate-500 uppercase tracking-wider">Target</div>
              <div className="text-3xl font-black mono leading-none" data-testid="whats-next-target-qty">
                {top.remaining_quantity}
                <span className="text-sm text-slate-400 font-normal">/{top.target_quantity}</span>
              </div>
              <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">units to build</div>
            </div>
            <Button
              size="lg"
              onClick={() => openWo(top.id)}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold rounded-sm px-6"
              data-testid="whats-next-open-btn"
            >
              Open <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </div>

        {nextUp.length > 0 && (
          <div className="border-t border-slate-100 px-5 py-3 bg-slate-50/70">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1">Up next</span>
              {nextUp.map(w => (
                <button
                  key={w.id}
                  onClick={() => openWo(w.id)}
                  className="group inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-slate-900 hover:bg-slate-900 hover:text-white px-3 py-1.5 rounded-sm text-xs transition-colors"
                  data-testid={`whats-next-chip-${w.wo_number || w.id}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${(STATUS_META[w.status] || STATUS_META.Draft).dot}`}></span>
                  <span className="font-semibold truncate max-w-[180px]">{w.bom_name}</span>
                  <span className="opacity-60 mono">· P{w.priority}</span>
                  <span className="opacity-60 mono">· {w.remaining_quantity}u</span>
                  <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WhatsNextHero;
